import React, { useState, useRef, useEffect } from 'react';
import { api, getRoleLabel, logout } from '../api';

export function ProfilPage({ user, setUser }) {
  const [form, setForm] = useState({ old_password: '', new_password: '', confirm: '' });
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: user?.name || '', email: user?.email || '', phone: user?.phone || '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [saving, setSaving] = useState(false);
  const [fotoUrl, setFotoUrl] = useState(user?.foto_url || null);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [showFotoModal, setShowFotoModal] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraMode, setCameraMode] = useState(false);

  useEffect(() => {
    // Fetch latest profile
    api.get('/auth/me').then(data => {
      if (data?.foto_url) setFotoUrl(data.foto_url);
    }).catch(() => {});
  }, []);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 3000);
  };

  const saveProfile = async () => {
    if (!editForm.name || !editForm.email) { showMsg('Nama dan email wajib diisi', 'error'); return; }
    setSavingEdit(true);
    try {
      await api.put('/auth/profile', { name: editForm.name, email: editForm.email, phone: editForm.phone });
      // Update localStorage
      const u = JSON.parse(localStorage.getItem('mig_user') || '{}');
      u.name = editForm.name; u.email = editForm.email; u.phone = editForm.phone;
      localStorage.setItem('mig_user', JSON.stringify(u));
      showMsg('Profil berhasil diupdate');
      setEditMode(false);
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setSavingEdit(false); }
  };

  const changePassword = async () => {
    if (!form.old_password || !form.new_password) { showMsg('Semua field wajib diisi', 'error'); return; }
    if (form.new_password !== form.confirm) { showMsg('Password baru tidak cocok', 'error'); return; }
    if (form.new_password.length < 6) { showMsg('Password minimal 6 karakter', 'error'); return; }
    setSaving(true);
    try {
      await api.put('/auth/change-password', { old_password: form.old_password, new_password: form.new_password });
      showMsg('Password berhasil diubah');
      setForm({ old_password: '', new_password: '', confirm: '' });
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setSaving(false); }
  };

  const uploadFoto = async (file) => {
    setUploadingFoto(true);
    try {
      const fd = new FormData();
      fd.append('foto', file);
      const data = await api.postForm('/users/me/foto', fd);
      setFotoUrl(data.foto_url);
      // Update user di localStorage
      const u = JSON.parse(localStorage.getItem('mig_user') || '{}');
      u.foto_url = data.foto_url;
      localStorage.setItem('mig_user', JSON.stringify(u));
      showMsg('Foto profil berhasil diupdate');
      setShowFotoModal(false);
    } catch (err) { showMsg(err.message || 'Gagal upload foto', 'error'); }
    finally { setUploadingFoto(false); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) uploadFoto(file);
  };

  const startCamera = async () => {
    setCameraMode(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch {
      showMsg('Kamera tidak tersedia', 'error');
      setCameraMode(false);
    }
  };

  const captureFromCamera = () => {
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    canvas.toBlob(blob => {
      stopCamera();
      uploadFoto(new File([blob], 'profil.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.9);
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    setCameraMode(false);
    setShowFotoModal(false);
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

  return (
    <div className="page fade-in">
      <h1 className="page-title">Profil</h1>
      <p className="page-subtitle">Informasi akun kamu</p>

      {msg.text && <div className={`toast toast-${msg.type}`}>{msg.text}</div>}

      {/* User Card dengan foto */}
      <div className="card" style={{ marginBottom: 20, textAlign: 'center' }}>
        {/* Foto Profil */}
        <div style={{ position: 'relative', width: 88, height: 88, margin: '0 auto 16px' }}>
          {fotoUrl ? (
            <img
              src={fotoUrl}
              alt="Foto Profil"
              style={{ width: 88, height: 88, borderRadius: 'var(--radius-lg)', objectFit: 'cover', border: '2px solid var(--border-hover)' }}
            />
          ) : (
            <div style={{ width: 88, height: 88, borderRadius: 'var(--radius-lg)', background: 'linear-gradient(135deg, var(--navy-500), var(--gold-glow))', border: '2px solid var(--border-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem', fontWeight: 800, color: 'var(--gold-400)' }}>
              {initials}
            </div>
          )}
          {/* Tombol edit foto */}
          <button
            onClick={() => setShowFotoModal(true)}
            style={{ position: 'absolute', bottom: -4, right: -4, width: 28, height: 28, borderRadius: '50%', background: 'var(--gold-500)', border: '2px solid var(--navy-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#0b1829' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
          </button>
        </div>

        {!editMode ? (
          <>
            <h2 style={{ marginBottom: 4 }}>{user?.name}</h2>
            <p style={{ fontSize: '0.85rem', marginBottom: 4 }}>{user?.email}</p>
            {user?.phone && <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 8 }}>📱 {user.phone}</p>}
            <span className="badge badge-gold">{getRoleLabel(user?.role)}</span>
            <button className="btn btn-outline btn-sm" style={{ marginTop: 12, width: '100%' }} onClick={() => { setEditForm({ name: user?.name || '', email: user?.email || '', phone: user?.phone || '' }); setEditMode(true); }}>
              ✏️ Edit Profil
            </button>
          </>
        ) : (
          <>
            <div className="input-group" style={{ textAlign: 'left', marginTop: 8 }}>
              <label className="input-label">Nama Lengkap</label>
              <input className="input" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="input-group" style={{ textAlign: 'left' }}>
              <label className="input-label">Email</label>
              <input className="input" type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="input-group" style={{ textAlign: 'left' }}>
              <label className="input-label">No. HP / WA (opsional)</label>
              <input className="input" placeholder="628xxx" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost btn-full btn-sm" onClick={() => setEditMode(false)}>Batal</button>
              <button className="btn btn-gold btn-full btn-sm" onClick={saveProfile} disabled={savingEdit}>{savingEdit ? '...' : 'Simpan'}</button>
            </div>
          </>
        )}
      </div>

      {/* Ganti Password */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16 }}>🔐 Ganti Password</h3>
        <div className="input-group">
          <label className="input-label">Password Lama</label>
          <input type="password" className="input" placeholder="••••••••" value={form.old_password} onChange={e => setForm(f => ({ ...f, old_password: e.target.value }))} />
        </div>
        <div className="input-group">
          <label className="input-label">Password Baru</label>
          <input type="password" className="input" placeholder="Min 6 karakter" value={form.new_password} onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))} />
        </div>
        <div className="input-group">
          <label className="input-label">Konfirmasi Password Baru</label>
          <input type="password" className="input" placeholder="••••••••" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} />
        </div>
        <button className="btn btn-gold btn-full" onClick={changePassword} disabled={saving}>
          {saving ? '...' : 'Simpan Password'}
        </button>
      </div>

      {/* Logout */}
      <button className="btn btn-danger btn-full btn-lg" onClick={logout}>
        🚪 Keluar
      </button>

      {/* Modal pilih sumber foto */}
      {showFotoModal && !cameraMode && (
        <div className="modal-overlay" onClick={() => setShowFotoModal(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 className="modal-title">Ganti Foto Profil</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button className="btn btn-gold btn-full btn-lg" onClick={startCamera}>
                📷 Ambil dari Kamera
              </button>
              <button className="btn btn-outline btn-full btn-lg" onClick={() => fileRef.current?.click()}>
                🖼️ Pilih dari Galeri
              </button>
              <button className="btn btn-ghost btn-full" onClick={() => setShowFotoModal(false)}>
                Batal
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelect} />
          </div>
        </div>
      )}

      {/* Modal kamera */}
      {cameraMode && (
        <div className="modal-overlay">
          <div className="modal-sheet">
            <div className="modal-handle" />
            <h2 className="modal-title">Ambil Foto Profil</h2>
            <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 16, aspectRatio: '1', background: '#000' }}>
              <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost btn-full" onClick={stopCamera}>Batal</button>
              <button className="btn btn-gold btn-full" onClick={captureFromCamera} disabled={uploadingFoto}>
                {uploadingFoto ? '...' : '📸 Ambil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
